/**
 * Model representing MCQ questions response structure.
 */

import Framework from '@greenville/framework';
import { types, getSnapshot, applySnapshot } from 'mobx-state-tree';
import { DEFAULT_STUDENT_ANSWER_CONFIDENCE } from '../common/Constants';
import AssignmentHelperUtils from '../../../common/utils/AssignmentHelperUtils';
import * as constants from '../../../common/constants';
import CommonUtils from '../../../common/utils/CommonUtils';

const Feedback = types.model('Feedback', {
  explanatory: types.optional(types.string, ''),
  corrective: types.optional(types.string, '')
});

const EvidenceText = types.model('EvidenceText', {
  quote: types.optional(types.string, ''),
  xpathSelector: types.optional(types.string, '')
});

const EvidenceImage = types.model('EvidenceImage', {
  path: types.optional(types.string, ''),
  xpathSelector: types.optional(types.string, '')
});

const EvidenceVideo = types.model('EvidenceVideo', {
  url: types.optional(types.string, ''),
  xpathSelector: types.optional(types.string, '')
});

const Evidence = types.model('Evidence', {
  text: types.optional(types.array(EvidenceText), []),
  images: types.optional(types.array(EvidenceImage), []),
  videos: types.optional(types.array(EvidenceVideo), [])
});


const Option = types.model('Option', {
  id: types.identifierNumber,
  feedback: types.union(types.string, Feedback),
  distractor: types.string,
  correctChoice: types.string
});

const AnswerAttempt = types.model('AnswerAttempt', {
  attemptNumber: types.number,
  answerNumber: types.union(types.number, types.string),
  isCorrect: types.boolean,
  attemptConfidence: types.optional(types.string, DEFAULT_STUDENT_ANSWER_CONFIDENCE),
  serverUpdatedTimeStamp: types.maybe(types.number)
});

const ExtraMaterialUsedType = types.model('ExtraMaterialUsedType', {
  type: types.string,
  frequency: types.maybe(types.number)
});

const Question = types.model('Question', {
  questionType: types.string,
  pageId: types.string,
  questionId: types.string,
  questionStem: types.string,
  elementId: types.string,
  relevantText: types.array(EvidenceText),
  evidence: types.optional(Evidence, {}),
  maxAttempts: types.optional(types.number, 3),
  options: types.array(Option),
  answerAttempts: types.optional(types.array(AnswerAttempt), []),
  extraMaterialUsedType: types.optional(types.array(ExtraMaterialUsedType), []),
  assignmentId: types.optional(types.string, ''),
  extraMaterialUsed: types.optional(types.boolean, false),
  answerConfidence: types.maybeNull(types.string),
  topic: types.optional(types.string, ''),
  statistics: types.optional(types.frozen(), {})
});

const MCQAssignments = types
  .model('MCQAssignments', {
    /** From GET /aet/books/{bookId}/mcqs response root (for BER feedback, etc.) */
    bookMcqsRequestId: types.optional(types.string, ''),
    bookMcqsResponseId: types.optional(types.string, ''),
    mcqQuestions: types.array(Question),
    assignmentMCQs: types.optional(types.frozen(), []),
    pendingStatusData: types.optional(types.frozen(), {})
  })
  .views(self => ({
    getMCQQuestionsPerAssignment: (assignmentId) => {
      const { mcqQuestions } = getSnapshot(self);

      if (mcqQuestions?.length <= 0) {
        return null;
      }

      const filteredQuestions = mcqQuestions.filter(question => question.assignmentId === assignmentId);

      return filteredQuestions?.length > 0 ? filteredQuestions : null;
    }
  }))
  .actions(self => ({
    /**
     * Reset assignment questions
     */
    resetAssignmentMCQs() {
      // eslint-disable-next-line no-param-reassign
      self.assignmentMCQs = [];
    },
    /**
     * Set assignment questions
     * @param {Object} data - Assignment questions data
     */
    setAssignmentMCQs(data) {
      console.log('~ setAssignmentQuestions', data);
      // eslint-disable-next-line no-param-reassign
      self.assignmentMCQs = data;
    },
    resetMCQData() {
      const resetQuestions = [...getSnapshot(self.mcqQuestions)];
      const renderResetQuestions = resetQuestions.map(question => ({
        ...question,
        assignmentId: '',
        answerAttempts: [],
        extraMaterialUsed: false,
        answerConfidence: '',
        extraMaterialUsedType: []
      }));
      applySnapshot(self.mcqQuestions, renderResetQuestions);
    },
    // Happens when MCQ questions for a book is fetched
    setMCQData(data) {
      // eslint-disable-next-line no-param-reassign
      self.mcqQuestions = data.mcqQuestions;
      // eslint-disable-next-line no-param-reassign
      self.bookMcqsRequestId = data.requestId || '';
      // eslint-disable-next-line no-param-reassign
      self.bookMcqsResponseId = data.responseId || '';

      // If we have pending status data, merge it now
      if (self.pendingStatusData && Object.keys(self.pendingStatusData).length > 0) {
        self.mergeStatusData(self.pendingStatusData);
        // eslint-disable-next-line no-param-reassign
        self.pendingStatusData = {};
      }
    },

    // Happens when MCQ status for a book and user is fetched
    setStatusData(statusData) {
      // If MCQ data is already loaded, merge immediately
      if (self.mcqQuestions.length > 0) {
        self.mergeStatusData(statusData);
      } else {
        // Store for later when MCQ data arrives
        // eslint-disable-next-line no-param-reassign
        self.pendingStatusData = statusData;
      }
    },
    convertAnswerKeyToNumber(attempt) {
      const { answerKey = '', answerNumber } = attempt;
      return typeof answerKey === 'string' ? CommonUtils.letterToNumber(answerKey) : answerNumber;
    },
    /**
     * Extract questions from status data
     *
     * @param {Object} statusData - Status data
     * @returns {Array} - Array of questions
     */
    extractQuestionsFromStatusData(statusData = {}) {
      const { assignments } = statusData;

      if (!assignments || !Array.isArray(assignments)) {
        return null;
      }

      const questions = assignments.flatMap((assignment) => {
        if (!assignment.assessment) {
          return null;
        }

        const { items } = assignment.assessment;

        if (!items || !Array.isArray(items)) {
          return null;
        }

        return items.map(item => ({
          assignmentId: assignment?.assignmentId,
          extraMaterialUsed: item?.mcqStatus?.extraMaterialUsed || false,
          extraMaterialUsedType: item?.mcqStatus?.extraMaterialUsedType || [],
          answerConfidence: item?.mcqStatus?.answerConfidence || '',
          answerAttempts: item?.mcqStatus?.answerAttempts || [],
          questionId: item?.activity?.activityId
        }));
      }).filter(Boolean);

      return questions;
    },
    mergeStatusData(statusData) {
      self.resetMCQData();

      const questions = self.extractQuestionsFromStatusData(statusData);
      if (!questions || !Array.isArray(questions)) {
        return;
      }

      questions.forEach((statusQuestion) => {
        const existingQuestion = self.mcqQuestions.find(qstn => qstn.questionId === statusQuestion.questionId);

        if (existingQuestion) {
          // Clear existing attempts and add new ones one by one
          existingQuestion.answerAttempts.clear();
          existingQuestion.assignmentId = statusQuestion?.assignmentId || '';
          existingQuestion.extraMaterialUsed = statusQuestion?.extraMaterialUsed || false;
          existingQuestion.extraMaterialUsedType = statusQuestion?.extraMaterialUsedType || [];
          existingQuestion.answerConfidence = statusQuestion?.answerConfidence || '';
          statusQuestion.answerAttempts?.forEach((attempt) => {
            const cleanAttempt = {
              attemptNumber: attempt.attemptNumber,
              answerNumber: self.convertAnswerKeyToNumber(attempt),
              isCorrect: attempt.isCorrect,
              attemptConfidence: attempt.attemptConfidence,
              serverUpdatedTimeStamp: attempt.serverUpdatedTimeStamp || 0
            };
            existingQuestion.answerAttempts.push(cleanAttempt);
          });
        }
      });
    },
    /**
     * Set statistics data for a question
     *
     * @param {string} questionId - Question ID
     * @param {Object} statisticsData - Statistics data
     */
    setStatisticsData(questionId, statisticsData) {
      if (!questionId || !statisticsData || self?.mcqQuestions?.length <= 0) {
        return;
      }

      const question = self.mcqQuestions.find(q => q.questionId === questionId);

      if (!question) {
        return;
      }

      question.statistics = statisticsData;
    }
  })).views(self => ({
    get questions() {
      return getSnapshot(self.mcqQuestions);
    },
    get assignmentQuestions() {
      const { mcqQuestions } = getSnapshot(self);

      return mcqQuestions.filter(question => question.assignmentId);
    },
    get reviewData() {
      const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
      const assignmentQuestions = self.assignmentQuestions;
      let hasReviewData = false;
      const ANSWERCONFIDENCE_KEY_PAIR = constants.ANSWERCONFIDENCE_KEY.map(statusKey => [
        AssignmentHelperUtils.normalizeStatus(statusKey),
        {
          id: statusKey,
          label: AssignmentHelperUtils.getSectionNameFromStatus(statusKey),
          topics: []
        }
      ]);
      const ANSWER_CONFIDENCE = new Map(ANSWERCONFIDENCE_KEY_PAIR);
      assignmentQuestions.forEach((question) => {
        const assignment = assignmentStore.getAssignmentById(question.assignmentId);
        const chapterContent = assignmentStore.getQuestionsByAssignmentId(question.assignmentId, question.questionId);
        if (!assignment || !chapterContent) {
          return;
        }
        const baseQuestion = AssignmentHelperUtils.getMcqData(question, chapterContent.chapterTitle);
        const status = question.answerConfidence.toLowerCase().replace(/\s/g, '');
        const topics = {
          name: question?.topic || chapterContent.chapterTitle,
          chapterTitle: chapterContent.chapterTitle,
          assignments: [{
            assignmentName: assignment.title,
            assignmentId: assignment.id,
            chapterId: chapterContent.chapterId || null,
            questions: [{
              ...baseQuestion,
              questionNumber: ''
            }]
          }]
        };
        if (ANSWER_CONFIDENCE.has(status)) {
          const data = ANSWER_CONFIDENCE.get(status);

          if (!data.topics.some(topic => topic.name === topics.name)) {
            data.topics.push(topics);
          }

          ANSWER_CONFIDENCE.set(status, data);
          hasReviewData = true;
        }
      });
      return hasReviewData ? Array.from(ANSWER_CONFIDENCE.values()) : [];
    },
    /**
     * Get statistics data for a question
     *
     * @param {string} questionId - Question ID
     * @returns {Object} - Statistics data
     */
    getStatisticsData(questionId) {
      if (!questionId || self?.mcqQuestions?.length <= 0) {
        return null;
      }

      const question = self.mcqQuestions.find(q => q.questionId === questionId);

      if (!question) {
        return null;
      }

      return question.statistics || null;
    }
  }));

export { MCQAssignments, Question, Option };
